import React, {
 useState,
 useMemo,
 useCallback,
 useEffect,
} from 'react'
import {
 useLocation,
 useSearchParams,
} from 'react-router-dom'
import {
 exportReportApi,
 getReportViewApi,
} from 'utils/api/reportService'
import {useListController} from 'hooks/useListController'
import KTTable from 'helpers/ui-components/KTTable'
import KTPagination from 'helpers/ui-components/KTPagination'
import {ColumnProps, IFormField} from 'models/Common'
import {useTranslation} from 'react-i18next'
import {PageTitle} from 'layout/master-layout'
import {fetchFilterConfig} from 'utils/api/auth/commonService'
import {createFilterPayload} from 'utils/helpers/genericFilterForm'
import {KTFilterBox} from 'helpers/ui-components/KTFilterBox'
import {ReportDetail} from 'models/Reports'
import Spinner from 'layout/common/Spinner'

interface Stakeholder {
 sub_type: string
 filter_url?: string
}

type TableRow = Record<string, unknown>

interface ReportColumn {
 key: string
 label: string
}
interface Props {
 selectedStakeholder: Stakeholder
 setSelectedStakeholder: (item: ReportDetail) => void

 filterConfig: IFormField[]
 setFilterConfig: React.Dispatch<
  React.SetStateAction<IFormField[]>
 >

 filterFormData: IFormField | null
 setFilterFormData: React.Dispatch<
  React.SetStateAction<IFormField | null>
 >
}

const ReportListing = ({
 selectedStakeholder,
 setSelectedStakeholder,
 filterConfig,
 setFilterConfig,
 filterFormData,
 setFilterFormData,
}: Props) => {
 const [searchParams] = useSearchParams()
 const {t} = useTranslation()
 const reportKey = searchParams.get('report_key')

 const [columns, setColumns] = useState<ReportColumn[]>([])

 const location = useLocation()

 const details = location.state?.details || []

 const subType = selectedStakeholder?.sub_type

 const [filterCount, setFilterCount] = useState(0)
 const [loader, setLoader] = useState(false)
 const getFilterConfig = async () => {
  if (!selectedStakeholder?.filter_url) return

  setLoader(true)

  const res = await fetchFilterConfig({
   filter_type: selectedStakeholder.filter_url,
   filter_key: subType,
  })

  if (res) {
   setFilterConfig(res?.applicableFilters ?? [])
  }

  setLoader(false)
 }

 const fetchReports = useCallback(
  async (page: number, pageSize: number) => {
   if (!reportKey || !subType) {
    return {data: [], total: 0}
   }
   const payload = await createFilterPayload(
    filterConfig,
    filterFormData
   )
   delete payload.totalAppliedFilters
   const res = await getReportViewApi({
    report_key: reportKey,
    sub_type: subType,
    page,
    page_size: pageSize,
    ...payload,
   })

   if (res.data?.columns) {
    setColumns(res.data.columns)
   }

   return {
    data: res.data?.results || [],
    total: res.data?.count || 0,
   }
  },
  [reportKey, subType, filterFormData, filterConfig]
 )
 const {
  data,
  total,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  reload,
 } = useListController({
  fetcher: fetchReports,
  initialPageSize: 10,
 })

 const tableColumns: Array<ColumnProps<TableRow>> =
  useMemo(() => {
   return columns.map((col) => ({
    key: col.key,
    title: col.label,
    className: 'text-left min-w-[100px]',
    render: (
     _: unknown,
     record: TableRow
    ): React.ReactNode => {
     const value = record[col.key]

     if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
     ) {
      return value
     }

     return '-'
    },
   }))
  }, [columns])

 useEffect(() => {
  const calculateFilters = async () => {
   if (!filterConfig) return

   const payload = await createFilterPayload(
    filterConfig,
    filterFormData
   )

   setFilterCount(payload.totalAppliedFilters || 0)
  }

  void calculateFilters()
 }, [filterFormData, filterConfig])

 useEffect(() => {
  if (!filterFormData) return

  setCurrentPage(1)
  void reload()
 }, [filterFormData])

 useEffect(() => {
  if (!selectedStakeholder) return

  setFilterFormData(null)
  setFilterCount(0)
  setCurrentPage(1)

  void getFilterConfig()
 }, [selectedStakeholder])

 return (
  <div>
   {loader && <Spinner />}
   <div className='flex items-center mb-4'>
    <div className='mb-4 w-full mt-2'>
     <KTFilterBox
      parentId={'Report_listing'}
      btnText={t('helperText.filter')}
      filterConfig={filterConfig}
      setLoader={setLoader}
      filterFormData={filterFormData}
      filterCount={filterCount}
      setFilterFormData={setFilterFormData}
     >
      {details.length > 1 && (
       <div className='flex items-center'>
        <div className='text-[#33393D] font-medium text-sm mr-2 ml-2'>
         {t('reports.StakeholderCategory')}
        </div>

        <div className='flex gap-2 flex-wrap'>
         {details.map((item: ReportDetail) => (
          <div
           key={item.sub_type}
           onClick={() => {
            setSelectedStakeholder(item)
           }}
           className={`${
            selectedStakeholder?.sub_type ===
             item.sub_type &&
            '!bg-[#4E83E4] text-white border-[#4E83E4]'
           } text-center py-1.5 px-3 bg-white 
      rounded-lg text-sm text-[#33393D] border font-medium border-[#CED8E0]
      hover:bg-[#4E83E4] hover:text-white cursor-pointer`}
          >
           {item.sub_type_display_name || item.sub_type}
          </div>
         ))}
        </div>
       </div>
      )}
     </KTFilterBox>
    </div>
   </div>

   <div className='mt-6 overflow-x-auto'>
    <KTTable
     data={data}
     columns={tableColumns}
     headerClass='bg-[#80868C]'
     rowClass='even:bg-[#F5F8FA]'
     colClass='!text-sm'
     showPagination={false}
    />
   </div>

   <div className='mt-4 flex flex-col'>
    <KTPagination
     currentPage={currentPage}
     itemsPerPage={itemsPerPage}
     totalRecord={total}
     onPageChange={setCurrentPage}
     setItemsPerPage={(size) => {
      setItemsPerPage(size)
      setCurrentPage(1)
     }}
    />
   </div>
  </div>
 )
}

export default function ReportListingWrapper() {
 const {t} = useTranslation()
 const [searchParams] = useSearchParams()
 const reportKey = searchParams.get('report_key')
 const location = useLocation()
 const details = location.state?.details || []
 const selectedFromParent =
  location.state?.selectedStakeholder

 const [selectedStakeholder, setSelectedStakeholder] =
  useState(selectedFromParent || details[0] || null)
 const [filterConfig, setFilterConfig] = useState<
  IFormField[]
 >([])
 const [filterFormData, setFilterFormData] =
  useState<IFormField | null>(null)
 const subType = selectedStakeholder?.sub_type
 const handleExport = async () => {
  if (!reportKey || !subType) return
  const payload = await createFilterPayload(
   filterConfig,
   filterFormData
  )
  const res = await exportReportApi({
   report_key: reportKey,
   sub_type: subType,
   page: 1,
   page_size: 1000,
   ...payload,
  })

  if (res.data?.presigned_url) {
   window.location.href = res.data.presigned_url
  }
 }

 return (
  <div className='container relative'>
   <div className='px-[72px] pt-6 pb-10 bg-white w-full min-h-full'>
    <div className='flex items-center justify-between'>
     <PageTitle
      breadcrumbContainerClass='flex text-[#5A6066] text-sm font-normal items-center pb-6 border-[#E6EBF0]'
      breadcrumbs={[
       {
        title: t('breadCrumbs.home'),
        path: '/home-page',
        isActive: false,
       },
       {
        title: t('breadCrumbs.quickLinks'),
        path: '/quick-links',
        isActive: false,
       },
       {
        title: t('breadCrumbs.reportListing'),
        path: '',
        isActive: true,
       },
      ]}
      description='Welcome'
     >
      {t('reports.title')}
     </PageTitle>

     <button
      onClick={handleExport}
      className='flex items-center gap-2 bg-[#3B82F6] text-white px-4 py-2 rounded-md text-sm hover:bg-[#2563EB]'
     >
      {t('reports.Download')}
     </button>
    </div>

    <ReportListing
     key={selectedStakeholder?.sub_type}
     selectedStakeholder={selectedStakeholder}
     setSelectedStakeholder={setSelectedStakeholder}
     filterConfig={filterConfig}
     setFilterConfig={setFilterConfig}
     filterFormData={filterFormData}
     setFilterFormData={setFilterFormData}
    />
   </div>
  </div>
 )
}
